import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AdminFormPage, saveErrorToast } from "@components/CatalogIndex";
import type { AdminFormToast } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { useV2GetProjectByIdQuery } from "@generated";
import { projectChromeQueryArgs } from "@components/SowHeader/projectChromeQuery";
import useChangeOrderAccess from "../hooks/useChangeOrderAccess";
import { useChangeOrderMutations } from "../api";
import ChangeOrderForm from "../ChangeOrderForm/ChangeOrderForm";
import { emptyChangeOrderForm } from "../types";
import type { ChangeOrderFormValues } from "../types";
import { changeOrderChild } from "../utils/changeOrderChild";

interface Props {
  projectId: string;
}

const FORM_ID = "change-order-form";

const New = ({ projectId }: Props) => {
  const navigate = useNavigate();
  const access = useChangeOrderAccess();
  const { create } = useChangeOrderMutations();
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<AdminFormToast | null>(null);

  // Shares the project chrome's cache entry.
  const { data: projectData } = useV2GetProjectByIdQuery(
    projectChromeQueryArgs(projectId)
  );
  const isChild = changeOrderChild(projectData?.data).isChangeOrder;

  // A change-order project can't have change orders of its own.
  if (!access.canCreate || isChild) return <Navigate to=".." replace />;

  const handleSubmit = async (values: ChangeOrderFormValues) => {
    setSubmitting(true);
    try {
      const created = await create(values, projectId);
      if (!values.referenceOnly && created.childProjectId) {
        // Full load: the project chrome is keyed to the project. The child's
        // change-order banner says which change order it is, so no toast.
        window.location.assign(
          `/projects/${created.childProjectId}/project_editor`
        );
        return;
      }
      navigate("..", {
        state: {
          toast: { variant: "success", text: `${created.label} created.` },
        },
      });
    } catch (err) {
      console.error("Failed to create change order", err);
      setToast(saveErrorToast(err, "change request", "add"));
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="New Change Request"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        isSubmitting={submitting}
      >
        <ChangeOrderForm
          id={FORM_ID}
          mode="new"
          projectId={projectId}
          initialValues={emptyChangeOrderForm}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default New;
